import { type ReactElement, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { format, isValid, parse } from 'date-fns';

import type {
  ResponsibilityFormEngProps,
  ResponsibilityFormProps,
  ResponsibilityFormUkrClimbingMouseProps,
  ResponsibilityFormUkrProps,
  ResponsibilityFormUkrZachipkaProps,
} from './types';

import {
  DISPLAY_DATES_FORMAT,
  FORM_LANG,
  MOUSE_SUBDOMAIN,
  SEND_DATES_FORMAT,
  SPACE_SUBDOMAIN,
  ZACHIPKA_SUBDOMAIN,
} from '@/constants';

import logo from './logo.svg';

const formatBirthday = (birthday: string | Date | undefined): string => {
  if (!birthday) {
    return '';
  }
  try {
    const date =
      typeof birthday === 'string'
        ? parse(birthday, SEND_DATES_FORMAT, new Date())
        : new Date(birthday);
    return isValid(date)
      ? format(date, DISPLAY_DATES_FORMAT)
      : String(birthday);
  } catch {
    return String(birthday);
  }
};

const formatCreatedAt = (createdAt: string | Date | undefined): string => {
  if (!createdAt) {
    return '';
  }
  try {
    const date =
      typeof createdAt === 'string'
        ? parse(createdAt, SEND_DATES_FORMAT, new Date())
        : new Date(createdAt);
    return isValid(date)
      ? format(date, DISPLAY_DATES_FORMAT)
      : String(createdAt);
  } catch {
    return String(createdAt) || '';
  }
};

export const ResponsibilityForm = (props: ResponsibilityFormProps) => {
  const { client, subdomain, printLang, onPrintComplete } = props;
  const hasPrintedRef = useRef(false);

  const renderResponsibilityFormUkr: Record<string, ReactElement> = {
    [MOUSE_SUBDOMAIN]: <ResponsibilityFormUkrClimbingMouse client={client} />,
    [SPACE_SUBDOMAIN]: (
      <ResponsibilityFormUkr
        client={client}
        logo={logo}
      />
    ),
    [ZACHIPKA_SUBDOMAIN]: <ResponsibilityFormUkrZachipka client={client} />,
  };

  const activeLang = printLang || FORM_LANG.uk;

  useLayoutEffect(() => {
    if (printLang && !hasPrintedRef.current) {
      hasPrintedRef.current = true;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.print();
          onPrintComplete();
          hasPrintedRef.current = false;
        });
      });
    }
  }, [printLang, onPrintComplete]);

  const portalContainer = document.getElementById('form-of-responsibility');

  if (!portalContainer) {
    return null;
  }

  return createPortal(
    <div className="hidden print:block print:text-lg">
      {activeLang === FORM_LANG.en && (
        <ResponsibilityFormEng
          client={client}
          logo={logo}
        />
      )}

      {activeLang === FORM_LANG.uk && renderResponsibilityFormUkr[subdomain]}
    </div>,
    portalContainer,
  );
};

const ResponsibilityFormUkr = (props: ResponsibilityFormUkrProps) => {
  const { client, logo } = props;

  return (
    <>
      <div className="mb-2">
        <img
          className="ml-auto block w-[150px]"
          src={logo}
          alt="Logo"
        />
        <h3 className="mt-5 mb-3 text-center text-lg font-bold">
          <span className="block text-right font-normal">ДОДАТОК №1 до</span>
          ПРАВИЛ ВІДВІДУВАННЯ ТА БЕЗПЕКИ СКЕЛЕДРОМІВ ТОВ «СПЕЙС КЛАЙМБІНГ» ФОРМА
          ВІДПОВІДАЛЬНОСТІ
        </h3>
      </div>
      <div className="clear-both" />
      <p className="mb-1.5 w-full text-base">
        Я, {client.first_name}&nbsp;{client.last_name}
      </p>
      {client.birthday ? (
        <p className="mb-1.5 w-full text-base">
          Дата народження: {formatBirthday(client.birthday)}
        </p>
      ) : (
        <p className="mb-1.5 flex w-full text-base">
          <span className="shrink-0 pr-2.5">Дата народження:</span>
          <span className="block w-full border-b border-black" />
        </p>
      )}
      <p className="mb-1.5 w-full text-base">
        Тел: {client.phone}. Email: {client.email}
      </p>
      <p className="my-3">
        Підписанням цієї Форми відповідальності підтверджую, що:
      </p>
      <ol className="mb-2 list-decimal pl-8 leading-tight">
        <li className="mb-1 text-base leading-tight">
          Обізнаний(а), що скелелазіння - травмонебезпечний вид спорту і
          заняття, яке може спричинити негативні наслідки, в тому числі, але не
          виключно травми різних ступенів тяжкості.
        </li>
        <li className="mb-1 text-base leading-tight">
          Ознайомлений з умовами Публічного договору про надання послуг ТОВ
          «СПЕЙС КЛАЙМБІНГ», Правилами відвідування та безпеки скеледромів ТОВ
          «СПЕЙС КЛАЙМБІНГ», а також з усією іншою інформацією, яка необхідна
          для повноцінного споживання послуг та зобовʼязуюсь неухильно їх
          дотримуватись. При цьому, підтверджую, що у разі внесення
          змін/редагування таких документів, такі вважаються доведеними до мого
          відома, а їх дія поширюється на мене, як на Замовника, незважаючи не
          те, що такі були прийняті після підписання цієї Форми.
        </li>
        <li className="mb-1 text-base leading-tight">
          У мене відсутні будь-які медичні протипоказання для занять
          скелелазінням та іншою фізичною активністю.
        </li>
        <li className="mb-1 text-base leading-tight">
          ТОВ «СПЕЙС КЛАЙМБІНГ» (ідентифікаційний код юридичної особи: 44502053)
          та/або Працівники Клубу (персонал Клубу, адміністрація, рецепція Клубу
          тощо) не несуть відповідальності в разі отримання мною травм або інших
          ушкоджень під час тренувань або перебування на території скеледрому
          (спортивного клубу).
        </li>
        <li className="mb-1 text-base leading-tight">
          ТОВ «СПЕЙС КЛАЙМБІНГ» та працівники Клубу не несуть відповідальність
          за забуті, загублені, викрадені або залишені без нагляду мною речі.
        </li>
        <li className="mb-1 text-base leading-tight">
          За наявність негативних (нещасних) випадків, у тому числі, але не
          виключно, повʼязаних із моїм здоровʼям, я самостійно несу
          відповідальність за їх настання.
        </li>
        <li className="mb-1 text-base leading-tight">
          Надаю згоду на використання фото- та відео-матеріалів, відзнятих на
          території Клубу, на розсуд уповноважених співробітників Клубу ТОВ
          «СПЕЙС КЛАЙМБІНГ», для використання у рекламних та інших цілях.
        </li>
        <li className="mb-1 text-base leading-tight">
          Обізнаний, що на території Клубу встановлені камери
          відеоспостереження.
        </li>
        <li className="mb-1 text-base leading-tight">
          Надані мною персональні дані, які зазначаються в цій Формі, необхідні
          для моєї ідентифікації, є моїм добровільним волевиявленням щодо
          дозволу на обробку моїх персональних даних та їх використання в рамках
          чинного законодавства України.
        </li>
      </ol>
      <div className="h-8" />
      <div className="mt-3 flex justify-between">
        <p>
          _________________________
          <span className="block text-xs">Дата</span>
        </p>
        <p>
          __________________
          <span className="block text-xs">Підпис</span>
        </p>
      </div>
    </>
  );
};

const ResponsibilityFormUkrClimbingMouse = (
  props: ResponsibilityFormUkrClimbingMouseProps,
) => {
  const { client } = props;

  return (
    <div className="block p-14 text-sm">
      <h3 className="mb-0 text-center font-bold">
        Заява про приєднання до публічного договору <br /> про надання послуг на
        скеледромі «Climbing Mouse» від {formatCreatedAt(client.created_at)}
      </h3>
      <h3 className="mb-0 text-center font-bold uppercase">
        Форма відповідальності
      </h3>
      <p className="m-0">
        Я, (Замовник) {client.last_name} {client.first_name}
      </p>
      <p className="m-0 flex">
        <span className="shrink-0 pr-2.5">РНОКПП</span>
        <span className="block w-full border-b border-black" />
      </p>
      <p className="m-0 flex">
        <span className="shrink-0 pr-2.5">Документ, що посвідчує особу</span>
        <span className="block w-full border-b border-black" />
      </p>
      {client.birthday ? (
        <p className="m-0">
          Дата народження (день/місяць/рік): {formatBirthday(client.birthday)}
        </p>
      ) : (
        <p className="m-0">Дата народження (день/місяць/рік):</p>
      )}
      <p className="m-0">Контактний номер телефону: {client.phone}</p>
      <p className="m-0">Електронна пошта: {client.email}</p>
      <p className="mt-4 mb-0 font-bold">
        Підписанням цієї Форми підтверджую, що:
      </p>
      <ol className="mt-0 mb-18 list-decimal pl-8 leading-tight">
        <li className="text-justify leading-tight">
          Обізнаний (а), що скелелазіння – травмонебезпечний вид спорту і
          заняття яким може спричинити негативні наслідки, в тому числі, але не
          виключно травми різних ступенів тяжкості та навіть смерть.
        </li>
        <li className="text-justify leading-tight">
          Ознайомлений з умовами Публічного договору про надання послуг ФОП
          Устименко Ксенія Олесандрівна, Правилами відвідування та безпеки
          скеледрому ФОП Устимекно Ксенія, а також з усією іншою інформацією,
          яка необхідна для повноцінного споживання послуг та зобов'язуюсь
          неухильно їх дотримуватись. При цьому, підтверджую, що у разі внесення
          змін/редагування таких документів, такі вважаються доведеними до мого
          відома, а їх дія поширюються на мене, як на Замовника, незважаючи на
          те, що такі були прийняті після підписання цієї Форми.
        </li>
        <li className="text-justify leading-tight">
          У мене відсутні будь-які медичні протипоказання для занять
          скелелазінням та іншою фізичною активністю.
        </li>
        <li className="text-justify leading-tight">
          Фізична особа-підприємець Устименко Ксенія Олександрівна
          (ідентифікаційний код: 3719000946) та/ або Працівники Клубу (персонал
          Клубу, адміністрація, рецепція Клубу тощо) не несуть відповідальності
          в разі отримання мною травм або інших ушкоджень під час тренувань або
          перебування на території скеледрому (спортивного клубу).
        </li>
        <li className="text-justify leading-tight">
          Фізична особа-підприємець Устименко Ксенія Олександрівна
          (ідентифікаційний код: 3719000946) та/ або Працівники Клубу (персонал
          Клубу, адміністрація, рецепція Клубу тощо) не несуть відповідальності
          за забуті, загублені, викрадені або залишені без нагляду мною речі.
        </li>
        <li className="text-justify leading-tight">
          За наявності негативних (нещасних) випадків, у тому числі, але не
          виключно, пов'язаних із моїм здоров'ям, я самостійно несу
          відповідальність за їх настання.
        </li>
        <li className="text-justify leading-tight">
          Надані мною персональні дані, які зазначаються в цій Формі, необхідні
          для моєї ідентифікації, є моїм добровільним волевиявленням щодо
          дозволу на обробку моїх персональних даних та їх використання в рамках
          чинного законодавства України.
        </li>
      </ol>
      <div className="flex justify-between">
        <p className="m-0 flex">
          <span className="shrink-0 pr-2.5">Дата</span>
          <span className="block w-25 border-b border-black" />
        </p>
        <p className="m-0 flex">
          <span className="shrink-0 pr-2.5">Підпис Замовника</span>
          <span className="block w-25 border-b border-black" />
        </p>
      </div>
    </div>
  );
};

const ResponsibilityFormUkrZachipka = (
  props: ResponsibilityFormUkrZachipkaProps,
) => {
  const { client } = props;
  return (
    <div className="block p-14 text-sm">
      <h3 className="mb-0 text-center font-bold">
        <span className="block text-right text-xs font-normal">ДОДАТОК №2</span>
        Заява про приєднання до публічного договору <br /> про надання послуг на
        скеледромі «Zachipka»
      </h3>
      <h3 className="mb-4 text-center font-bold uppercase">
        Форма відповідальності
      </h3>
      <p className="m-0">
        Я, {client.last_name} {client.first_name}
      </p>
      <p className="m-0 flex items-end">
        <span className="shrink-0 pr-2.5">РНОКПП</span>
        <span className="mb-0.5 block w-full border-b border-black" />
      </p>
      <p className="m-0 flex items-end">
        <span className="shrink-0 pr-2.5">Документ, що посвідчує особу</span>
        <span className="mb-0.5 block w-full border-b border-black" />
      </p>
      {client.birthday ? (
        <p className="m-0">
          Дата народження: {formatBirthday(client.birthday)}
        </p>
      ) : (
        <p className="m-0 flex">
          <span className="shrink-0 pr-2.5">Дата народження:</span>
          <span className="block w-full border-b border-black" />
        </p>
      )}
      <p className="m-0">Контактний номер телефону: {client.phone}</p>
      <p className="m-0">Електронна пошта: {client.email}</p>
      <p className="mt-4 mb-0 font-bold">
        Підписанням цієї Форми відповідальності підтверджую, що:
      </p>
      <ol className="mt-0 mb-18 list-decimal pl-8 leading-tight">
        <li className="text-justify leading-tight">
          Обізнаний (а), що скелелазіння – травмонебезпечний вид спорту і
          заняття яким може спричинити негативні наслідки, в тому числі, але не
          виключно травми різних ступенів тяжкості та навіть смерть.
        </li>
        <li className="text-justify leading-tight">
          Ознайомлений з умовами Публічного договору про надання послуг ФОП
          Яськевич Борис Дмитрович, Правилами відвідування та безпеки скеледрому
          ФОП Яськевич Борис Дмитрович, а також з усією іншою інформацією, яка
          необхідна для повноцінного споживання послуг та зобов&#39;язуюсь
          неухильно їх дотримуватись. При цьому, підтверджую, що у разі внесення
          змін/редагування таких документів, такі вважаються доведеними до мого
          відома, а їх дія поширюються на мене, як на Замовника, незважаючи на
          те, що такі були прийняті після підписання цієї Форми.
        </li>
        <li className="text-justify leading-tight">
          У мене відсутні будь-які медичні протипоказання для занять
          скелелазінням та іншою фізичною активністю.
        </li>
        <li className="text-justify leading-tight">
          Фізична особа-підприємець Яськевич Борис Дмитрович (ідентифікаційний
          код: 3604110330) та/ або Працівники Клубу (персонал Клубу,
          адміністрація, рецепція Клубу тощо) не несуть відповідальності в разі
          отримання мною травм або інших ушкоджень під час тренувань або
          перебування на території скеледрому (спортивного клубу).
        </li>
        <li className="text-justify leading-tight">
          Фізична особа-підприємець Яськевич Борис Дмитрович (ідентифікаційний
          код: 3604110330) та/ або Працівники Клубу (персонал Клубу,
          адміністрація, рецепція Клубу тощо) не несуть відповідальності за
          забуті, загублені, викрадені або залишені без нагляду мною речі.
        </li>
        <li className="text-justify leading-tight">
          За наявності негативних (нещасних) випадків, у тому числі, але не
          виключно, пов&#39;язаних із моїм здоров&#39;ям, я самостійно несу
          відповідальність за їх настання.
        </li>
        <li className="text-justify leading-tight">
          Надані мною персональні дані, які зазначаються в цій Формі, необхідні
          для моєї ідентифікації, є моїм добровільним волевиявленням щодо
          дозволу на обробку моїх персональних даних та їх використання в рамках
          чинного законодавства України.
        </li>
      </ol>
      <div className="flex justify-between">
        <p className="m-0">Дата {format(new Date(), DISPLAY_DATES_FORMAT)}</p>
        <p className="m-0 flex">
          <span className="shrink-0 pr-2.5">Підпис Замовника</span>
          <span className="block w-25 border-b border-black" />
        </p>
      </div>
    </div>
  );
};

const ResponsibilityFormEng = (props: ResponsibilityFormEngProps) => {
  const { client, logo } = props;

  return (
    <>
      <div className="mb-2">
        <img
          className="ml-auto block w-[150px]"
          src={logo}
          alt="Logo"
        />
        <h3 className="mt-5 mb-3 text-center text-lg font-bold">
          <span className="block text-right font-normal">
            APPENDIX No. 1 to
          </span>
          RULES OF VISITING AND SAFETY OF CLIMBING GYMS OF "SPACE CLIMBING" LLC
          FORM OF RESPONSIBILITY
        </h3>
      </div>
      <div className="clear-both" />
      <p className="mb-1.5 w-full text-base">
        Name, Surname: {client.first_name}&nbsp;{client.last_name}
      </p>
      {client.birthday ? (
        <p className="mb-1.5 w-full text-base">
          Date of birth: {formatBirthday(client.birthday)}
        </p>
      ) : (
        <p className="mb-1.5 flex w-full text-base">
          <span className="shrink-0 pr-2.5">Date of birth:</span>
          <span className="block w-full border-b border-black" />
        </p>
      )}
      <p className="mb-1.5 w-full text-base">
        Phone: {client.phone}. Email: {client.email}
      </p>
      <p className="my-3">
        By signing this Form of Responsibility, I confirm that:
      </p>
      <ol className="mb-2 list-decimal pl-8 leading-tight">
        <li className="mb-1 text-base leading-tight">
          I am aware and have informed the minor that rock climbing is a
          dangerous sport and activity that may result in negative consequences,
          including but not limited to injuries of varying degrees of severity.
        </li>
        <li className="mb-1 text-base leading-tight">
          I have read the terms of the Public Contract for the provision of
          services by "SPACE CLIMBING" LLC, the Rules of Visiting and Safety of
          climbing gyms of "SPACE CLIMBING" LLC, as well as all other
          information necessary for the full consumption of services and
          undertake to strictly comply with them. I also confirm that in case of
          any changes/amendments to such documents, they shall be deemed brought
          to my attention, and their effect shall extend to me as a Client,
          regardless of whether they were adopted after signing this Form.
        </li>
        <li className="mb-1 text-base leading-tight">
          I have no medical contraindications for rock climbing and other
          physical activities.
        </li>
        <li className="mb-1 text-base leading-tight">
          "SPACE CLIMBING" LLC (legal entity identification code: 44502053)
          and/or Club Employees (Club staff, administration, Club reception,
          etc.) shall not be liable in case I sustain injuries or other damages
          during training or while on the premises of the climbing gym (sports
          club).
        </li>
        <li className="mb-1 text-base leading-tight">
          "SPACE CLIMBING" LLC and Club employees shall not be liable for my
          forgotten, lost, stolen or unattended belongings.
        </li>
        <li className="mb-1 text-base leading-tight">
          In case of any negative (unfortunate) incidents, including but not
          limited to those related to my health, I am solely responsible for
          their occurrence.
        </li>
        <li className="mb-1 text-base leading-tight">
          I consent to the use of photo and video materials taken on the Club
          premises, at the discretion of authorized employees of "SPACE
          CLIMBING" LLC Club, for use in advertising and other purposes.
        </li>
        <li className="mb-1 text-base leading-tight">
          I am aware that video surveillance cameras are installed on the Club
          premises.
        </li>
        <li className="mb-1 text-base leading-tight">
          The personal data provided by me, which is specified in this Form,
          necessary for my identification, is my voluntary expression of will to
          allow the processing of my personal data and their use within the
          framework of the current legislation of Ukraine.
        </li>
      </ol>
      <div className="h-8" />
      <div className="mt-3 flex justify-between">
        <p>
          _________________________
          <span className="block text-xs">Date</span>
        </p>
        <p>
          __________________
          <span className="block text-xs">Signature</span>
        </p>
      </div>
    </>
  );
};
